import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { AppConfigApiService } from '@core/services/api/app-config-api.service';

const ACCESS_DENIED_PATH = '/dashboard/access-denied';

/**
 * Marks a request whose 403 the calling screen renders itself - a consent gate such
 * as FRI CPR's `ConsentNotGrantedException`, not a missing permission. The error
 * still reaches the caller; only the redirect to access denied is skipped, and the
 * once-per-session redirect gate is left untouched for a genuine 403 later.
 */
export const FORBIDDEN_IS_SCREEN_STATE = new HttpContextToken<boolean>(() => false);

/**
 * Until the signed-in user enrolls a face, the API answers every route outside the
 * enrolment with 403 `face_enrollment_required`. That is not a missing permission:
 * the dashboard shell opens normally and its facial-enrolment gate asks for the photo,
 * so the redirect to access denied is skipped and the once-per-session gate is left
 * untouched for a genuine 403 later.
 */
const FACE_ENROLLMENT_REQUIRED = 'face_enrollment_required';

function isFaceEnrollmentRequired(error: HttpErrorResponse): boolean {
  const body: unknown = error.error;
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === FACE_ENROLLMENT_REQUIRED
  );
}

let accessDeniedHandled = false;

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly configApi = inject(AppConfigApiService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          this.routeStatusError(request, error);
          return throwError(() => error);
        }

        return throwError(() => error);
      }),
    );
  }

  private routeStatusError(request: HttpRequest<unknown>, error: HttpErrorResponse): void {
    const url = request.url;
    const status = error.status;

    if (!this.isApiRequest(url)) {
      return;
    }

    if (status === 403) {
      if (!request.context.get(FORBIDDEN_IS_SCREEN_STATE) && !isFaceEnrollmentRequired(error)) {
        this.navigateAccessDeniedOnce();
      }
      return;
    }

    if (
      status === 404 &&
      this.isAnalysesRequest(url) &&
      !this.isVcrModuleRequest(url) &&
      !this.isOrchestrationRequest(url) &&
      !this.isConsentRequest(url) &&
      !this.isApprovalRequest(url)
    ) {
      void this.router.navigate(['/404'], { queryParams: { context: 'analysis' } });
    }
  }

  private navigateAccessDeniedOnce(): void {
    const currentPath = this.router.url.split('?')[0] ?? this.router.url;

    if (currentPath === ACCESS_DENIED_PATH || accessDeniedHandled) {
      return;
    }

    accessDeniedHandled = true;
    void this.router.navigate([ACCESS_DENIED_PATH]);
  }

  private isApiRequest(url: string): boolean {
    const apiBaseUrl = this.configApi.getApiBaseUrl();

    return url.startsWith('/api') || url.startsWith('api/') || url.startsWith(apiBaseUrl);
  }

  private isAnalysesRequest(url: string): boolean {
    const pathname = this.extractPathname(url);

    return /\/analyses(?:\/|$)/.test(pathname);
  }

  /**
   * VCR module reads (`.../vcr/mri`, `.../vcr/fri`, `.../vcr/lri`, axes, indicators).
   * A 404 here means the module/slice was not computed or is unknown - screen state -
   * not that the analysis itself is missing. The detail shell preloads FRI/MRI on
   * entry; hard-routing those 404s to `/404?context=analysis` bounces the user off
   * a valid detail page.
   */
  private isVcrModuleRequest(url: string): boolean {
    const pathname = this.extractPathname(url);

    return /\/analyses\/[^/]+\/vcr(?:\/|$)/.test(pathname);
  }

  /**
   * Consent reads and mutations (`.../consents/signers`, `.../consents`, and the
   * `.../consents/{tipo}/...` writes). Etapa 5 loads `GET /analyses/{id}` in the same
   * forkJoin as the roster, and that request is not exempt - a missing analysis still
   * hard-routes through it. So a 404 coming back only from a consent route is screen state
   * (roster, consent list, or one dispatch), and the authorization screen already renders
   * it per section. Hard-routing those bounces the user off a page whose analysis exists.
   */
  private isConsentRequest(url: string): boolean {
    const pathname = this.extractPathname(url);

    return /\/analyses\/[^/]+\/consents(?:\/|$)/.test(pathname);
  }

  /**
   * The approval chain of an analysis (`.../approval`, and the reads and acts
   * under it). `GET /analyses/{id}/approval` answers **404 `no-chain`** when the
   * derivation legitimately did not run - an empty alçada catalogue, a committee
   * without members - and Guia Autorizações §0.1 is explicit that this means
   * "there is no chain yet", not a missing analysis. The detail screen renders it
   * itself; hard-routing it would bounce the user off an analysis that exists.
   */
  private isApprovalRequest(url: string): boolean {
    const pathname = this.extractPathname(url);

    return /\/analyses\/[^/]+\/approval(?:\/|$)/.test(pathname);
  }

  /** Emission polling may 404 briefly; keep the processing screen, do not hard-route to app 404. */
  private isOrchestrationRequest(url: string): boolean {
    const pathname = this.extractPathname(url);

    return /\/analyses\/[^/]+\/orchestration(?:\/|$)/.test(pathname);
  }

  private extractPathname(url: string): string {
    try {
      return new URL(url, 'http://localhost').pathname;
    } catch {
      return url.split('?')[0] ?? url;
    }
  }
}

/** Test helper — and re-login hook — resets the 403 navigation gate. */
export function resetApiErrorInterceptorState(): void {
  accessDeniedHandled = false;
}
